import React, { useEffect, useState } from 'react';
import { simboloMoneda, getNegocioId } from '../services/negocioActual';
import { armarPlanHoy, crearRecordatorio, cambiarEstadoEstrategia } from '../services/metasService';
import { obtenerCampanaActiva, obtenerEnvios } from '../services/recompraService';
import { resumenParaIA } from '../utils/metasResumen';
import { perfilesRecompra, inicioDia, GRUPOS } from '../utils/recompra';
import { DIAS_SEMANA, fechaYMD } from '../utils/metas';
import { mensajeAmigable } from '../utils/errores';
import { useToast } from '../context/ToastContext';

const ACENTO = 'var(--color-primario,#DB2777)';
const clave = () => `kontrol.planHoy.${getNegocioId()}.${fechaYMD(new Date())}`;
const leer = () => { try { return JSON.parse(localStorage.getItem(clave()) || 'null'); } catch { return null; } };
const norm = (t) => String(t || '').trim().toLowerCase();

// 📅 ¿Qué hago hoy?: la IA ordena las estrategias del mes en un plan del día
// con horario, junta las que se repiten, propone una oferta corta y deja los
// recordatorios listos con un toque. El plan del día se guarda en este equipo.
export default function MetasPlanHoy({ resumen, meta, estrategias, setEstrategias, recordatorios, datos, negocio, onRecordatorioCreado }) {
  const { mostrarToast } = useToast();
  const s = simboloMoneda();
  const [plan, setPlan] = useState(leer);
  const [pensando, setPensando] = useState(false);
  const [campana, setCampana] = useState(null);
  const [hechos, setHechos] = useState({ recordatorios: false, marcha: false });

  // La campaña de recompra entra al plan (cuántas faltan por escribir en cada grupo).
  useEffect(() => {
    let activo = true;
    obtenerCampanaActiva().then(async (c) => {
      if (!c || !activo) return;
      const env = await obtenerEnvios(c.id);
      const enviados = new Set(env.map((e) => e.cliente_id));
      const perfiles = perfilesRecompra(datos.clientes || [], (datos.ventas || []).filter((v) => v.cliente_id), inicioDia(c.desde));
      const porEscribir = Object.fromEntries(GRUPOS.map((g) => [g.etiqueta, perfiles.filter((p) => p.grupo === g.clave && !p.volvio && !enviados.has(p.cliente.id)).length]));
      const ayer = new Date(); ayer.setDate(ayer.getDate() - 1);
      if (activo) setCampana({
        descuento_de_clienta: `${Number(c.descuento_pct)}%`, valido_hasta: c.hasta, mensajes_enviados: env.length,
        enviados_ayer: env.filter((e) => new Date(e.enviado_en).toDateString() === ayer.toDateString()).length,
        volvieron_a_comprar: perfiles.filter((p) => p.volvio && enviados.has(p.cliente.id)).length,
        por_escribir_por_grupo: porEscribir,
      });
    }).catch(() => {});
    return () => { activo = false; };
  }, [datos]);

  const pedir = async () => {
    if (!meta) { mostrarToast('Primero guarda tu meta del mes', 'error'); return; }
    setPensando(true);
    try {
      const ahora = new Date();
      const base = resumenParaIA(resumen, { negocio, meta, simbolo: s, estrategiasActuales: [] });
      const r = await armarPlanHoy({
        ...base,
        hoy: { dia: DIAS_SEMANA[ahora.getDay()].nombre, fecha: fechaYMD(ahora), hora_actual: ahora.toTimeString().slice(0, 5) },
        estrategias_del_mes: estrategias.map((e) => ({ titulo: e.titulo, estado: { pendiente: 'por hacer', en_curso: 'en marcha', hecha: 'hecha' }[e.estado], detalle: (e.detalle || '').slice(0, 300) })),
        pendientes_de_hoy_ya_agendados: recordatorios.filter((x) => !x.hecho && new Date(x.cuando).toDateString() === ahora.toDateString())
          .map((x) => `${new Date(x.cuando).toTimeString().slice(0, 5)} ${x.titulo}`),
        campana_de_recompra: campana,
      });
      setPlan(r);
      setHechos({ recordatorios: false, marcha: false });
      try { localStorage.setItem(clave(), JSON.stringify(r)); } catch { /* sin almacenamiento */ }
    } catch (e) { mostrarToast(e.message || 'No se pudo armar el plan', 'error', 6000); } finally { setPensando(false); }
  };

  const estrategiaPorTitulo = (t) => estrategias.find((e) => norm(e.titulo) === norm(t));

  // ¿Ya hay un recordatorio de hoy con ese título? (al recargar la página o tocar dos
  // veces, antes se volvían a crear todos y salían duplicados).
  const yaAgendado = (titulo) => {
    const hoy = new Date().toDateString();
    return recordatorios.some((x) => !x.hecho && norm(x.titulo) === norm(titulo) && new Date(x.cuando).toDateString() === hoy);
  };
  const todosAgendados = !!plan?.bloques?.length && plan.bloques.every((b) => yaAgendado(b.titulo));
  const [creando, setCreando] = useState(false);

  const crearRecordatorios = async () => {
    if (creando) return;
    setCreando(true);
    const ahora = new Date();
    let n = 0;
    for (const b of plan.bloques) {
      const [h, m] = b.hora.split(':').map(Number);
      const cuando = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate(), h, m);
      if (cuando < new Date(ahora.getTime() - 60000)) continue; // ya pasó
      if (yaAgendado(b.titulo)) continue; // ya estaba
      try {
        const r = await crearRecordatorio({ titulo: b.titulo, nota: `${b.que_hacer}${b.donde ? ` · ${b.donde}` : ''}`, cuando, estrategiaId: estrategiaPorTitulo(b.estrategia)?.id });
        onRecordatorioCreado && onRecordatorioCreado(r);
        n += 1;
      } catch (e) { mostrarToast(mensajeAmigable(e), 'error'); break; }
    }
    setHechos((x) => ({ ...x, recordatorios: true }));
    setCreando(false);
    mostrarToast(n ? `⏰ Listo: ${n} recordatorio(s) para hoy. Te suenan en el celular` : 'Ya estaban creados (o esas horas ya pasaron)', n ? 'exito' : 'info', 5000);
  };

  const ponerEnMarcha = async () => {
    const lista = (plan.en_marcha || []).map(estrategiaPorTitulo).filter((e) => e && e.estado === 'pendiente');
    for (const e of lista) {
      try { await cambiarEstadoEstrategia(e.id, 'en_curso'); } catch (err) { mostrarToast(mensajeAmigable(err), 'error'); return; }
    }
    setEstrategias((x) => x.map((e) => (lista.some((l) => l.id === e.id) ? { ...e, estado: 'en_curso' } : e)));
    setHechos((x) => ({ ...x, marcha: true }));
    mostrarToast(lista.length ? `🟡 ${lista.length} estrategia(s) en marcha` : 'Esas estrategias ya estaban en marcha', 'exito');
  };

  const porMarcar = (plan?.en_marcha || []).map(estrategiaPorTitulo).filter((e) => e && e.estado === 'pendiente').length;

  return (
    <div className="k-card" style={{ padding: '18px', marginBottom: '12px', border: '1px solid #DDD6FE', background: 'linear-gradient(135deg,#FFFFFF 0%,#F5F3FF 100%)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div>
          <p style={{ margin: 0, fontWeight: 800, fontSize: '16px' }}>📅 ¿Qué hago hoy?</p>
          <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#6B7280' }}>Kontrol ordena tus estrategias en un plan para hoy, con horario, para que empieces a vender ya.</p>
        </div>
        <button onClick={pedir} disabled={pensando || !estrategias.length}
          title={estrategias.length ? '' : 'Primero agrega estrategias a tu plan'}
          style={{ padding: '10px 16px', borderRadius: '12px', border: 'none', background: 'linear-gradient(90deg,#6D28D9,#DB2777)', color: 'white', fontWeight: 800, cursor: pensando ? 'wait' : 'pointer', opacity: estrategias.length ? 1 : 0.5, fontFamily: 'inherit' }}>
          {pensando ? '📅 Armando tu día… (menos de un minuto)' : plan ? '🔄 Volver a armarlo' : '📅 Armar mi plan de hoy'}
        </button>
      </div>
      {!estrategias.length && <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#9CA3AF' }}>Agrega estrategias en la pestaña 🧭 Estrategias para poder armar tu día.</p>}

      {plan && (
        <div style={{ marginTop: '14px' }}>
          <p style={{ margin: '0 0 10px', fontSize: '14px', fontWeight: 700, color: '#1F1A2E' }}>🎯 {plan.foco}</p>
          {plan.oferta && (
            <div style={{ background: '#FDF2F8', border: '1px solid #FBCFE8', borderRadius: '12px', padding: '10px 12px', marginBottom: '10px' }}>
              <b style={{ color: '#9D174D' }}>🎁 Oferta del día: {plan.oferta.titulo}</b>
              <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#831843' }}>{plan.oferta.detalle}</p>
            </div>
          )}
          <div style={{ position: 'relative', paddingLeft: '4px' }}>
            {plan.bloques.map((b, i) => (
              <div key={i} style={{ display: 'flex', gap: '12px', marginBottom: '10px' }}>
                <span style={{ flex: '0 0 auto', background: ACENTO, color: 'white', fontWeight: 800, fontSize: '12px', borderRadius: '8px', padding: '4px 8px', height: 'fit-content', fontFamily: "'DM Mono', monospace" }}>{b.hora}</span>
                <div style={{ background: 'white', border: '1px solid #EEE9F8', borderRadius: '12px', padding: '8px 12px', flex: 1 }}>
                  <b style={{ fontSize: '14px' }}>{b.titulo}</b>
                  <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#374151' }}>{b.que_hacer}</p>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6B7280' }}>
                    {b.donde && <>📍 {b.donde}</>}{b.estrategia && estrategiaPorTitulo(b.estrategia) && <> · 🧭 {b.estrategia}</>}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '6px 0 10px' }}>
            <button onClick={crearRecordatorios} disabled={hechos.recordatorios || todosAgendados || creando}
              style={{ padding: '9px 14px', borderRadius: '10px', border: 'none', background: hechos.recordatorios || todosAgendados ? '#D1FAE5' : '#D97706', color: hechos.recordatorios || todosAgendados ? '#065F46' : 'white', fontWeight: 700, cursor: 'pointer' }}>
              {hechos.recordatorios || todosAgendados ? '✓ Recordatorios creados' : creando ? 'Creando…' : '⏰ Crear los recordatorios de hoy'}
            </button>
            {porMarcar > 0 && !hechos.marcha && (
              <button onClick={ponerEnMarcha} style={{ padding: '9px 14px', borderRadius: '10px', border: '1px solid #FCD34D', background: '#FEF3C7', color: '#92400E', fontWeight: 700, cursor: 'pointer' }}>
                🟡 Poner en marcha ({porMarcar})
              </button>
            )}
          </div>
          {plan.despues?.length > 0 && (
            <div style={{ fontSize: '13px', color: '#4B5563' }}>
              <b>⏭️ Para después:</b>
              <ul style={{ margin: '4px 0 0', paddingLeft: '20px' }}>{plan.despues.map((d, i) => <li key={i}>{d.titulo} — <b>{d.cuando}</b></li>)}</ul>
            </div>
          )}
          {plan.animo && <p style={{ margin: '10px 0 0', fontWeight: 700, color: '#5B21B6' }}>💜 {plan.animo}</p>}
          {typeof plan.quedan === 'number' && <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#9CA3AF' }}>Puedes rearmarlo {plan.quedan} vez/veces más hoy.</p>}
        </div>
      )}
    </div>
  );
}
